import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { refineScanMiss } from '@/lib/taxRateScan';
import { taxRateSchema } from './expenseLineItemSchema';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { parseLocalISODate, formatLocalISODate } from '@/lib/dateUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AccountSelect } from '@/components/accounting/ui/account-select';
import { useShowAccountCurrency } from '@/components/accounting/ui/useAccountLabelHelpers';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Building, User, AlertTriangle, HelpCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FileAttachments } from '@/components/ui/file-attachments';
import { useCreateAPInvoice, useUpdateAPInvoice, useApproveAPInvoice, useRepointAPPayments, buildAPBillCreateBody, hasFeedLinkedPayment, APInvoice } from '@/hooks/useAccountsPayable';
import { CurrencyDisplay } from '@/components/ui/currency-display';
import { useChartOfAccounts } from '@/hooks/useChartOfAccounts';
import { useBusinesses } from '@/hooks/useBusinesses';
import { usePersonIndex } from '@/hooks/usePeople';
import { useProjects } from '@/hooks/useProjects';
import { useFunds } from '@/hooks/useFunds';
import { FundSelect } from '@/components/accounting/ui/fund-select';
import { useOrganization } from '@/contexts/OrganizationContext';
import { calculateLineTotalWithTax, resolveTaxTreatment, toTaxRateType } from '@/lib/taxCalculations';
import { recurringPayloadSchemaByType, type NlWkrCategory, type RecurringTemplateWire } from '@releve/shared';
import { useNlWkrSourceCategories } from '@/hooks/useNlWkr';
import { getAccountSignForForm } from '@/lib/account-category-utils';
import { InvoiceLineItems, TaxRateType } from './InvoiceLineItems';
import { useSuspenseAccountBalance } from '@/hooks/useSuspenseAccountBalance';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronRight } from 'lucide-react';
import { hasUserLineDirtyLeaf } from '@/lib/formDirty';
import { useCarbonTracking } from '@/hooks/useCarbonTracking';
import { useUploadTransactionAttachments, useTransactionAttachments, useDeleteTransactionAttachment } from '@/hooks/useTransactionAttachments';
import { captureException } from '@/lib/sentry';
import { toast } from '@/hooks/useToast';
import { isTaxRateRefusal, toastTaxRateRefusal } from '@/lib/taxRateRefusal';
import { useTranslation } from '@/hooks/useTranslation';
import { Trans } from 'react-i18next';
import { useRenderDebug } from '@/hooks/useRenderDebug';
import type { ChartOfAccount } from '@/types/financial-accounts';
import type { FileAttachment } from '@/components/ui/file-attachments';
import { PAYMENT_TERMS_OPTIONS, calculateDueDate } from '@/lib/paymentTerms';
import { mapParsedDocumentToBillForm, convertLineItemAmounts, buildConversionMemoNote } from '@/lib/parsedDocumentMapper';
import { useParsedDocumentConversion } from '@/hooks/useParsedDocumentConversion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import type { ParsedDocument } from '@/types/parsedDocument';
import { useOrganizationBusinessIdentity } from '@/hooks/useOrganizationBusinessIdentity';
import { useFullTaxSettings } from '@/hooks/useOrganizationTaxSettings';
import { useBusinessVendorIdentity } from '@/hooks/useBusinessVendorIdentity';
import { OrganizationIdentityDisplay, VendorIdentityDisplay } from './BusinessIdentityDisplay';
import { EntityImage } from '@/components/ui/entity-image';
import { DangerZone } from '@/components/ui/danger-zone';
import { RecurringSection } from './recurring/RecurringSection';
import {
  recurringFormFields,
  recurringFormRefinement,
  resolveRecurringDefaults,
  type RecurringFormFields,
} from './recurring/recurringFormFields';
import { useRecurringTemplateSubmit } from './recurring/useRecurringTemplateSubmit';
import { buildRecurringPayload, hydrateAPBillFromPayload } from '@/lib/recurringPayload';
import {
  AP_BILL_NUMBER_TOKEN_DEFAULT,
  RECURRING_TEXT_TOKENS_HINT_VALUE,
  dueDaysFromTerms,
  todayInOrgZone,
  type RecurrenceFormSchemaContext,
  suggestTemplateName,
} from '@/lib/recurrence';

const EMPTY_ACCOUNTS: ChartOfAccount[] = [];

// The purchase-line tax rate (a number, 'exempt' or 'non_reclaimable') —
// one definition shared with the expense form.

const lineItemSchema = z.object({
  expense_account_id: z.string().min(1, 'Expense account is required'),
  description: z.string().min(1, 'Description is required'),
  quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
  unit_price: z.number().min(0, 'Unit price must be positive'),
  tax_rate: taxRateSchema,
  tax_rate_id: z.string().optional(),
  tax_inclusive: z.boolean().default(false),
  is_reverse_charge: z.boolean().default(false),
  exemption_reason: z.string().optional(),
  exemption_reason_other: z.string().optional(),
  /** Placeholder-rate marker for the default-rate fill (form-only, never sent). */
  tax_rate_unset: z.boolean().optional(),
  /** Scanned percentage the fill pass matches (form-only, never sent). */
  tax_rate_hint: z.number().optional(),
  /** The hint is tax ÷ subtotal — matched within ±0.01 (form-only, never sent). */
  tax_rate_hint_computed: z.boolean().optional(),
  /** The scanned VAT amount for the line (form-only, never sent) — the "VAT not reclaimable" gross-up. */
  tax_rate_hint_vat: z.number().optional(),
  tax_rate_hint_vat_base: z.number().optional(),
  /** The net unit price while grossed up for "VAT not reclaimable" (form-only, never sent). */
  tax_rate_hint_net: z.number().optional(),
  /** The unit price the gross-up wrote (form-only, never sent). */
  tax_rate_hint_gross: z.number().optional(),
  /** The user stated the amount after a gross-up — never gross up again (form-only, never sent). */
  tax_rate_hint_amount_kept: z.boolean().optional(),
  /** The user picked in the tax picker ("No VAT" included) — clears the scan-miss block (form-only, never sent). */
  tax_rate_confirmed: z.boolean().optional(),
  /** NL WKR toggle — reveals the category sub-row (form-local, never wired). */
  wkr_enabled: z.boolean().optional(),
  /** NL WKR classification ('' = none); jurisdiction-gated in the UI. */
  wkr_category: z.string().optional(),
}).superRefine(refineScanMiss);

const formSchema = z.object({
  payee_type: z.enum(['business', 'person'], { required_error: 'Please select payee type' }),
  business_id: z.string().optional(),
  business_name: z.string().optional(), // For custom vendor name input
  person_id: z.string().optional(),
  invoice_number: z.string().min(1, 'Invoice number is required'),
  invoice_date: z.string().min(1, 'Invoice date is required'),
  due_date: z.string().min(1, 'Due date is required'),
  payment_terms: z.string().optional(),
  memo: z.string().optional(),
  reference: z.string().optional(),
  ap_account_id: z.string().min(1, 'Accounts Payable account is required'),
  project_id: z.string().optional(),
  fund_id: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, 'At least one line item is required'),
  // Advanced VAT Options
  self_billing: z.boolean().default(false),
  additional_vat_notes: z.string().optional(),
  // "Make this recurring" — form-local toggle + the schedule (see RecurringSection).
  ...recurringFormFields,
}).refine((data) => {
  if (data.payee_type === 'business') {
    return (data.business_id && data.business_id.length > 0) || (data.business_name && data.business_name.trim().length > 0);
  } else {
    return data.person_id && data.person_id.length > 0;
  }
}, {
  message: 'Please select or enter a vendor name, or select a person',
  path: ['payee_type'],
}).refine((data) => {
  // Both fields are plain calendar days (yyyy-MM-dd), so a lexicographic
  // compare IS a chronological compare — no Date round-trip, no timezone.
  return data.due_date >= data.invoice_date;
}, {
  message: 'Due date must be on or after invoice date',
  path: ['due_date'],
});

type FormData = z.infer<typeof formSchema>;

/**
 * The resolver schema — the base shape plus the context-dependent schedule
 * rules (org-today, due days, a start locked after the first run). The
 * context is read through `getContext` at validation time, so the schema
 * object itself is built once per mount.
 */
const buildFormSchema = (getContext: () => RecurrenceFormSchemaContext) =>
  formSchema.superRefine(recurringFormRefinement(getContext));

interface CreateAPInvoiceFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  invoiceId?: string;
  invoice?: APInvoice;
  onSubmitReady?: (submitHandler: () => void) => void;
  initialAttachments?: Array<{ file: File; name: string }>;
  initialParsedData?: ParsedDocument | null;
  onInvoiceCreated?: (result: { invoice_id: string; success: boolean }) => void | Promise<void>;
  /**
   * Show the "Make this recurring" section (feature-flag gated inside). Only
   * the Payables tab, the quick-add panel and the recurring page pass it —
   * the inbox / import hosts do not.
   */
  allowRecurring?: boolean;
  /** Open with the recurring box already ticked (the "New recurring…" menu). */
  defaultRecurring?: boolean;
  /**
   * Template-edit mode: hydrate the document from the template's payload and
   * the schedule from its rule; saving PATCHes the template in place.
   */
  recurringTemplate?: RecurringTemplateWire | null;
  /** Fires when the user ticks/unticks the box — hosts swap the footer label. */
  onRecurringChange?: (enabled: boolean) => void;
  /**
   * After a recurring CREATE the form navigates to `/accounting/recurring`
   * (replace) before closing; the recurring page itself passes `false`.
   */
  navigateOnSuccess?: boolean;
  /** Template-edit mode: the DangerZone's action — the host owns the confirm + delete. */
  onRequestDelete?: () => void;
  /**
   * Reports the save in flight, so a host can disable its footer (no double
   * submit). Same contract as `CreateExpenseForm`.
   */
  onSubmittingChange?: (isPending: boolean) => void;
  /**
   * Reports whether the user changed anything (fields, new attachments,
   * manual carbon), so a host can skip "Discard changes?" on a pristine form.
   * Same contract as `CreateExpenseForm` (`useLiftedFormDialogState`).
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * Clone mode ("Duplicate" / "Make recurring" from a bill's expanded row):
   * hydrate payee, lines, accounts, tax, project, fund and memo from an
   * EXISTING bill, but save as a NEW one — no id, today's date, the due date
   * kept the same number of days out, an empty bill number. Attachments,
   * carbon and budget tags are not copied. Mutually exclusive with
   * `invoiceId` / `recurringTemplate`.
   */
  cloneFrom?: APInvoice | null;
  /**
   * Preset the project on a NEW bill (the project dashboard's Add menu).
   * Seeds `defaultValues` only: ignored in edit mode, and any source that
   * carries its own project — a recurring template, a clone — still
   * overrides it (parsed-document data carries no project, so it keeps the
   * preset). The user can change or clear it.
   */
  defaultProjectId?: string;
}

export const CreateAPInvoiceForm = ({
  onSuccess,
  onCancel,
  invoiceId,
  invoice,
  onSubmitReady,
  initialAttachments,
  initialParsedData,
  onInvoiceCreated,
  allowRecurring = false,
  defaultRecurring = false,
  recurringTemplate = null,
  cloneFrom = null,
  onRecurringChange,
  navigateOnSuccess,
  onSubmittingChange,
  onDirtyChange,
  onRequestDelete,
  defaultProjectId,
}: CreateAPInvoiceFormProps) => {
  const { t } = useTranslation('common');
  const { selectedOrganization } = useOrganization();
  const createAPInvoice = useCreateAPInvoice();
  const updateAPInvoice = useUpdateAPInvoice();
  const approveAPInvoice = useApproveAPInvoice();
  const repointAPPayments = useRepointAPPayments();
  const uploadAttachments = useUploadTransactionAttachments();
  const deleteAttachment = useDeleteTransactionAttachment();
  const isEditMode = !!invoiceId;
  const isTemplateMode = !!recurringTemplate;
  const { submit: submitRecurringTemplate } = useRecurringTemplateSubmit();

  // The ONE "today" the schedule uses — the org's calendar day, so the form and
  // the DB's `start >= org-today` guard agree (docs/DATES.md: day-math over an instant).
  const orgToday = useMemo(
    () => todayInOrgZone(selectedOrganization?.timezone),
    [selectedOrganization?.timezone],
  );

  // Template-edit mode: the stored payload is jsonb — re-validate before hydrating.
  const templatePayload = useMemo(() => {
    if (!recurringTemplate) return null;
    const parsed = recurringPayloadSchemaByType.ap_bill.safeParse(recurringTemplate.payload);
    if (!parsed.success) {
      captureException(new Error('Recurring AP template payload failed validation'), {
        context: 'AP recurring template hydrate',
        templateId: recurringTemplate.id,
        issues: parsed.error.issues.map((i) => i.path.join('.')).join(','),
      });
      return null;
    }
    return parsed.data;
  }, [recurringTemplate]);

  // Read at validation time by the resolver (see buildFormSchema) — a ref, so
  // the schema stays stable while org-today / the template mode may change.
  const recurrenceContextRef = useRef<RecurrenceFormSchemaContext>({ orgToday, documentType: 'ap_bill' });
  recurrenceContextRef.current = {
    orgToday,
    documentType: 'ap_bill',
    originalStartDate: recurringTemplate?.start_date ?? null,
    startLocked: (recurringTemplate?.occurrences_generated ?? 0) > 0,
  };
  const resolverSchema = useMemo(() => buildFormSchema(() => recurrenceContextRef.current), []);

  const { data: accounts, isLoading: isLoadingAccounts } = useChartOfAccounts(selectedOrganization?.id);
  const showAccountCurrency = useShowAccountCurrency(accounts);
  // NL WKR: existing classifications keyed by invoice_line_items.id — merged
  // into the edit-load reset by LINE ID (never array position). Disabled (and
  // instantly settled) outside gated NL orgs.
  const { data: wkrByLineId, isLoading: isLoadingWkr } = useNlWkrSourceCategories(
    selectedOrganization?.id,
    'ap_bill',
    isEditMode ? invoiceId : cloneFrom?.id,
  );
  const { data: businesses, isLoading: isLoadingBusinesses } = useBusinesses(selectedOrganization?.id);
  const { data: people, isLoading: isLoadingPeople } = usePersonIndex(selectedOrganization?.id, { sortByName: true });
  const { data: projects, isLoading: isLoadingProjects } = useProjects(selectedOrganization?.id);
  const { data: funds, isLoading: isLoadingFunds } = useFunds(selectedOrganization?.id);
  const { accountId: suspenseAccountId } = useSuspenseAccountBalance(selectedOrganization?.id);

  // Business identity hooks (org-level — no dependency on form state)
  const { data: orgIdentity } = useOrganizationBusinessIdentity(selectedOrganization?.id);
  const { data: taxSettings } = useFullTaxSettings(selectedOrganization?.id);

  // Load existing attachments if editing
  const { data: existingAttachments } = useTransactionAttachments(
    invoice?.ledger_transaction_id,
    invoice?.id
  );

  // Check if all data is loaded (especially important for edit mode)
  const isLoadingData = isLoadingAccounts || isLoadingBusinesses || isLoadingPeople || isLoadingProjects || isLoadingFunds;
  // Edit AND clone mode paint only once hydrated (no blank-then-snap).
  const [isFormReady, setIsFormReady] = useState(!isEditMode && !cloneFrom);

  // File attachments state
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  // Advanced VAT Options state
  const [vatOptionsOpen, setVatOptionsOpen] = useState(false);

  // Re-approval confirmation dialog state
  const [showReapprovalConfirm, setShowReapprovalConfirm] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const [currentApprovalStatus, setCurrentApprovalStatus] = useState<string | null>(null);
  // Default ON: re-approve the bill immediately after a force-edit so it never
  // lingers in 'pending' with a dangling (posted-but-unapplied) payment. When a
  // paid bill is edited and this is left unchecked, the dialog warns that the
  // payment will dangle until the bill is re-approved.
  const [autoReapprove, setAutoReapprove] = useState(true);
  // "Move payments" dialog: opened when a paid bill's edit changes the AP
  // account/fund (PAYMENT_ACCOUNT_CHANGE). Confirming migrates the payments to
  // the new account (reverse + re-post) and re-approves — see handleConfirmedMovePayments.
  const [showMovePaymentsConfirm, setShowMovePaymentsConfirm] = useState(false);
  // An import-linked payment can't be reversed (it's undone from the bank feed),
  // so its account can't be auto-migrated. Detect up front to keep the bill out
  // of a half-edited state — the RPC also refuses (IMPORT_LINKED) as a backstop.
  const hasImportLinkedPayment = useMemo(
    // Shared with the approve-time resolution dialog (APApproveMismatchDialog)
    // so both surfaces agree on what "from the feed" means.
    () => hasFeedLinkedPayment(invoice),
    [invoice],
  );

  // Recurring defaults go through `defaultValues` (never a mount-time
  // setValue, which would flip isDirty and arm "Discard changes?").
  const recurringDefaults: RecurringFormFields = resolveRecurringDefaults({
    documentType: 'ap_bill',
    orgToday,
    template: recurringTemplate,
    defaultRecurring,
    dueDays: 30,
  });
  // Template-edit mode seeds the (disabled) date fields from the schedule so
  // the base `.min(1)` + `due_date >= invoice_date` rules still pass; the
  // payload strip drops them anyway.
  const templateStart = recurringTemplate?.start_date;
  const templateStartDate = templateStart ? parseLocalISODate(templateStart) : null;
  const templateDue =
    templateStartDate && recurringTemplate
      ? formatLocalISODate(addDays(templateStartDate, recurringTemplate.due_days ?? 0)) ?? templateStart
      : undefined;

  const form = useForm<FormData>({
    resolver: zodResolver(resolverSchema),
    defaultValues: {
      payee_type: 'business' as 'business' | 'person',
      business_id: '',
      business_name: '',
      person_id: '',
      invoice_number: defaultRecurring && !recurringTemplate ? AP_BILL_NUMBER_TOKEN_DEFAULT : '',
      // `accounts_payable.invoice_date` is a plain `date` column — today's
      // LOCAL calendar day, never an instant.
      invoice_date: templateStart ?? format(new Date(), 'yyyy-MM-dd'),
      due_date: templateDue ?? calculateDueDate(new Date(), 'net_30') ?? '',
      payment_terms: 'net_30',
      memo: '',
      reference: '',
      ap_account_id: '',
      project_id: (!invoiceId && defaultProjectId) || '',
      fund_id: '',
      line_items: [
        {
          expense_account_id: '',
          description: '',
          quantity: 1,
          unit_price: 0,
          tax_rate: 0 as TaxRateType,
          tax_rate_id: '',
          tax_inclusive: false,
          is_reverse_charge: false,
          exemption_reason: '',
          exemption_reason_other: '',
          // Placeholder 0 — InvoiceLineItems fills the org default rate.
          tax_rate_unset: true,
        },
      ],
      // Advanced VAT Options
      self_billing: false,
      additional_vat_notes: '',
      // Template-edit mode: the document half comes from the stored payload.
      ...(templatePayload ? hydrateAPBillFromPayload(templatePayload) : {}),
      ...recurringDefaults,
    },
  });

  const stableAccounts = accounts ?? EMPTY_ACCOUNTS;

  const payeeType = form.watch('payee_type');
  const lineItems = form.watch('line_items');
  // While ticked, the bill's dates are set by the schedule (fields disabled,
  // values kept so the base schema passes; the payload strip drops them).
  const recurringEnabled = form.watch('recurring_enabled');
  const selectedBusinessId = form.watch('business_id');
  const selectedPersonId = form.watch('person_id');

  // Vendor identity hook (depends on form state above)
  const { data: vendorIdentity } = useBusinessVendorIdentity(
    payeeType === 'business' ? selectedBusinessId : undefined
  );

  // Get selected vendor info for display card
  const selectedVendorInfo = useMemo(() => {
    if (payeeType === 'business' && selectedBusinessId) {
      const biz = businesses?.find(b => b.id === selectedBusinessId);
      if (biz) return { name: biz.name, email: biz.contact_email };
    } else if (payeeType === 'person' && selectedPersonId) {
      const person = people?.find(p => p.id === selectedPersonId);
      if (person) return { name: `${person.first_name} ${person.last_name}`, email: person.email };
    }
    return null;
  }, [payeeType, selectedBusinessId, selectedPersonId, businesses, people]);

  // Handle payment terms change -> auto-update due date
  const handlePaymentTermsChange = useCallback((termsValue: string) => {
    form.setValue('payment_terms', termsValue);
    // invoice_date is a plain calendar day now — parse it directly.
    const baseDate = parseLocalISODate(form.getValues('invoice_date')) ?? new Date();
    const dueDate = calculateDueDate(baseDate, termsValue);
    if (dueDate) {
      form.setValue('due_date', dueDate);
    }
  }, [form]);

  // Handle invoice date change -> re-derive due date from the current terms
  const handleInvoiceDateChange = useCallback((date: Date | undefined) => {
    if (!date) return;
    const dueDate = calculateDueDate(date, form.getValues('payment_terms'));
    if (dueDate) {
      form.setValue('due_date', dueDate);
    }
  }, [form]);

  // Watch line_items so carbon tracking remaps when the form populates on
  // edit (form.reset may land after the carbon fetch completes).
  const watchedLineItems = form.watch('line_items');
  const carbonLineItemAccountIds = useMemo(
    () => watchedLineItems?.map(item => item?.expense_account_id) ?? [],
    [watchedLineItems],
  );

  // Carbon tracking integration
  const {
    carbonDataByLineItem,
    updateCarbonData,
    clearCarbonData,
    saveCarbonEmissions,
    hasManualCarbonEdits,
  } = useCarbonTracking({
    organizationId: selectedOrganization?.id || '',
    transactionId: invoice?.ledger_transaction_id,
    // AP bills only get a ledger transaction on approval; key carbon by the
    // invoice id so pre-approval data is retained and graduated on approve.
    apInvoiceId: invoice?.id,
    lineItemAccountIds: carbonLineItemAccountIds,
  });

  // DEBUG: Track what's causing re-renders
  useRenderDebug('CreateAPInvoiceForm', {
    selectedOrganization,
    accounts,
    businesses,
    people,
    projects,
    funds,
    existingAttachments,
    isLoadingData,
    attachments,
    carbonDataByLineItem,
  });

  // Load existing attachments into state when they're fetched
  useEffect(() => {
    if (existingAttachments && existingAttachments.length > 0) {
      const transformedAttachments = existingAttachments.map((attachment) => ({
        id: attachment.id,
        existingFile: attachment.file,
      }));
      setAttachments(transformedAttachments);
    }
  }, [existingAttachments]);

  // Load initial attachments from props (for inbox integration)
  useEffect(() => {
    if (initialAttachments && initialAttachments.length > 0 && !invoiceId) {
      const transformedAttachments = initialAttachments.map((attachment, index) => ({
        id: `initial-${index}`,
        file: attachment.file,
      }));
      setAttachments(transformedAttachments);
    }
  }, [initialAttachments, invoiceId]);

  // When the scanned document is denominated in a foreign currency (e.g. a EUR
  // vendor invoice in a USD-books org), look up the ECB rate so the prefill
  // below can populate ESTIMATED org-currency amounts.
  const documentConversion = useParsedDocumentConversion({
    parsedData: initialParsedData,
    targetCurrency: selectedOrganization?.currency,
    enabled: !!initialParsedData && !invoiceId,
  });
  const [conversionBanner, setConversionBanner] = useState<
    | { type: 'estimated'; docCurrency: string; rate: number; rateDate?: string }
    | { type: 'unavailable'; docCurrency: string }
    | null
  >(null);
  // Report the save in flight and the user's edits to the host dialog
  // (`useLiftedFormDialogState`). dirtyFields, not the aggregate isDirty:
  // programmatic fills (payment terms / due date, the default AP account, the
  // org-default tax rate on lines) must not arm "Discard changes?" — only a
  // `true` leaf outside the line shadow fields counts (lib/formDirty).
  const isMutationPending = createAPInvoice.isPending || updateAPInvoice.isPending;
  useEffect(() => {
    onSubmittingChange?.(isMutationPending);
  }, [isMutationPending, onSubmittingChange]);
  const hasUnsavedEdits = Boolean(
    hasUserLineDirtyLeaf(form.formState.dirtyFields) ||
      // A file the user added — not the inbox's prefilled `initial-*` ones,
      // not the bill's existing attachments (those carry no `file`).
      attachments.some((a) => a.file && !a.id?.startsWith('initial-')) ||
      hasManualCarbonEdits,
  );
  useEffect(() => {
    onDirtyChange?.(hasUnsavedEdits);
  }, [hasUnsavedEdits, onDirtyChange]);

  // Read during render so the RHF formState proxy tracks isDirty; the prefill
  // effect checks it imperatively before applying a late (post-rate) reset.
  const { isDirty: isFormDirty } = form.formState;
  // Once-guard: the prefill may wait on the rate query, so the effect re-runs;
  // each initialParsedData object (memoized by callers) is applied exactly once.
  const appliedParsedDataRef = useRef<ParsedDocument | null>(null);

  // Pre-populate form from AI-extracted document data (for inbox integration).
  // With a foreign-currency document the reset is held until the exchange-rate
  // lookup settles, then amounts are converted (or left raw with a warning
  // banner when no rate is available).
  useEffect(() => {
    if (!initialParsedData || invoiceId) return;
    if (appliedParsedDataRef.current === initialParsedData) return;
    if (documentConversion.status === 'pending') return;

    const mappedData = mapParsedDocumentToBillForm(initialParsedData);
    if (!mappedData) return;

    appliedParsedDataRef.current = initialParsedData;

    // The rate arrived after the user already started editing — don't clobber
    // their input (and don't claim a conversion happened).
    if (isFormDirty) return;

    let lineItems = mappedData.line_items;
    let memo = mappedData.memo || '';
    if (documentConversion.status === 'converted' && documentConversion.rate !== undefined) {
      lineItems = convertLineItemAmounts(lineItems, documentConversion.rate);
      const note = buildConversionMemoNote(
        initialParsedData.total,
        documentConversion.docCurrency!,
        documentConversion.rate,
        documentConversion.rateDate,
      );
      memo = memo ? `${memo}\n${note}` : note;
      setConversionBanner({
        type: 'estimated',
        docCurrency: documentConversion.docCurrency!,
        rate: documentConversion.rate,
        rateDate: documentConversion.rateDate,
      });
    } else if (documentConversion.status === 'unavailable') {
      setConversionBanner({ type: 'unavailable', docCurrency: documentConversion.docCurrency! });
    }

    form.reset({
      ...form.getValues(),
      ...(mappedData.business_name && { business_name: mappedData.business_name }),
      ...(mappedData.invoice_number && { invoice_number: mappedData.invoice_number }),
      ...(mappedData.invoice_date && { invoice_date: mappedData.invoice_date }),
      ...(mappedData.payment_terms && { payment_terms: mappedData.payment_terms }),
      ...(memo && { memo }),
      line_items: lineItems.map(item => ({
        ...item,
        tax_rate: item.tax_rate as TaxRateType,
      })),
    });
    // Recalculate due_date from invoice_date + payment_terms — must stay
    // coupled to the reset above (it derives from the just-reset invoice_date).
    if (mappedData.payment_terms) {
      handlePaymentTermsChange(mappedData.payment_terms);
    }
  }, [
    initialParsedData,
    invoiceId,
    documentConversion.status,
    documentConversion.rate,
    documentConversion.rateDate,
    documentConversion.docCurrency,
    isFormDirty,
    form,
    handlePaymentTermsChange,
  ]);

  // Handle deletion of existing attachments
  const handleDeleteExisting = useCallback(async (attachmentId: string) => {
    if (!invoice?.id || !selectedOrganization?.id) return;
    try {
      await deleteAttachment.mutateAsync({
        organizationId: selectedOrganization.id,
        attachmentId,
        transactionId: invoice.ledger_transaction_id,
        apInvoiceId: invoice.id,
      });
    } catch (error) {
      toast.warning(t('billing.toast.deleteAttachmentFailed'));
      captureException(error as Error, {
        context: 'AP invoice attachment delete',
        invoiceId: invoice?.id,
      });
    }
  }, [invoice?.id, invoice?.ledger_transaction_id, selectedOrganization?.id, deleteAttachment]);

  /** Build the invoice data payload from form data, shared by submit and re-approval paths. */
  const buildInvoiceData = useCallback((data: FormData) => {
    const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

    let businessId: string | undefined;
    if (data.payee_type === 'business') {
      if (data.business_id) {
        businessId = data.business_id;
      } else if (data.business_name && isUUID(data.business_name)) {
        businessId = data.business_name;
      }
    }

    // Sign-aware sum so the stored amount_total matches the displayed total
    // when a line points to a non-Expense account (e.g. a Revenue refund
    // line on an AP bill). Mirrors the LineItemTotals math in
    // InvoiceLineItems and the SQL account_sign_for_form() used by the
    // approve/post RPC. SCRUM-142 part 2.
    const computedTotal = data.line_items.reduce((sum, item) => {
      const { tax_rate } = resolveTaxTreatment(item.tax_rate);
      const account = stableAccounts.find(a => a.id === item.expense_account_id);
      const sign = getAccountSignForForm(account, 'ap');
      return sum + sign * calculateLineTotalWithTax(item.unit_price, tax_rate, item.quantity);
    }, 0);

    return {
      business_id: businessId,
      person_id: data.payee_type === 'person' ? data.person_id : undefined,
      invoice_number: data.invoice_number,
      invoice_date: data.invoice_date,
      due_date: data.due_date,
      description: data.memo || undefined,
      purchase_order_number: data.reference || undefined,
      project_id: data.project_id === 'none' || !data.project_id ? undefined : data.project_id,
      amount_total: computedTotal,
      ap_account_id: data.ap_account_id,
      fund_id: data.fund_id === 'none' || !data.fund_id ? undefined : data.fund_id,
      line_items: data.line_items.map((item, index) => {
        const { tax_rate, tax_treatment } = resolveTaxTreatment(item.tax_rate, item.is_reverse_charge);
        return {
          expense_account_id: item.expense_account_id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate,
          tax_rate_id: item.tax_rate_id || undefined,
          tax_treatment,
          tax_inclusive: false,
          exemption_reason: item.exemption_reason || undefined,
          exemption_reason_other: item.exemption_reason_other || undefined,
          sort_order: index,
          // NL WKR: rides the line payload into nl_wkr_sync_from_lines —
          // only when the line's WKR checkbox is on (belt to the uncheck-
          // clears-category handler).
          wkr_category: ((item.wkr_enabled && item.wkr_category) || undefined) as
            | NlWkrCategory
            | undefined,
        };
      }),
    };
  }, [stableAccounts]);

  // Use a ref to hold the latest submit function to avoid stale closures
  const onSubmitRef = useRef<(data: FormData) => Promise<void>>(undefined);

  onSubmitRef.current = async (data: FormData) => {
    if (!selectedOrganization?.id) return;

    // "Make this recurring": save a TEMPLATE, not a bill. The payload is the
    // exact create wire body minus its dates; the shared chain creates (or
    // PATCHes) it, runs it when due today, and navigates to the list. The
    // side channels (carbon, attachments) are skipped — nothing was created
    // to attach them to. Failures are toasted by the mutations themselves.
    if (data.recurring_enabled && data.recurrence) {
      try {
        const payload = buildRecurringPayload('ap_bill', buildAPBillCreateBody(buildInvoiceData(data)));
        await submitRecurringTemplate({
          organizationId: selectedOrganization.id,
          documentType: 'ap_bill',
          recurrence: data.recurrence,
          payload,
          templateId: recurringTemplate?.id,
          navigateOnSuccess,
        });
        clearCarbonData();
        setAttachments([]);
        form.reset();
        onSuccess?.();
      } catch {
        // Toasted + captured by the recurring mutations (MutationCache.onError).
      }
      return;
    }

    try {
      const invoiceData = buildInvoiceData(data);

      let createResult: { success: boolean; invoice_id: string } | undefined;

      if (isEditMode && invoiceId) {
        const result = await updateAPInvoice.mutateAsync({
          organizationId: selectedOrganization!.id,
          invoiceId: invoiceId,
          data: invoiceData,
        });

        // Update carbon emissions. Key by invoice id (always present) plus the
        // ledger transaction id when the bill is already approved — so carbon on
        // a still-pending bill persists too, not just approved bills.
        if (invoice?.id) {
          try {
            const carbonKeys = {
              transactionId: invoice.ledger_transaction_id ?? undefined,
              apInvoiceId: invoice.id,
            };
            // The unified RPC deletes prior rows by key before inserting.
            await saveCarbonEmissions(carbonKeys, data.line_items);
          } catch (carbonError) {
            captureException(carbonError as Error, {
              context: 'AP invoice carbon emissions update',
              invoiceId,
              ledgerTransactionId: invoice.ledger_transaction_id,
            });
          }
        }

        // Upload new attachments in edit mode
        const newFiles = attachments.filter(a => a.file).map(a => a.file);
        if (newFiles.length > 0) {
          try {
            await uploadAttachments.mutateAsync({
              organizationId: selectedOrganization.id,
              transactionId: invoice?.ledger_transaction_id,
              apInvoiceId: invoiceId,
              files: newFiles,
              attachmentType: 'invoice',
              entityType: 'ap-invoices',
              entityId: invoiceId,
            });
          } catch (attachmentError) {
            toast.warning(t('billing.toast.someAttachmentsFailed'));
            captureException(attachmentError as Error, {
              context: 'AP invoice attachment upload (edit mode)',
              invoiceId,
              fileCount: newFiles.length,
            });
          }
        }
      } else {
        const result = await createAPInvoice.mutateAsync({
          organizationId: selectedOrganization.id,
          data: invoiceData,
        });
        createResult = result;

        // AP invoice creation doesn't post to the ledger (that happens on
        // approval), so key carbon by the new invoice id. approve_ap_invoice
        // graduates these rows onto the ledger transaction it creates.
        if (result?.invoice_id) {
          try {
            await saveCarbonEmissions({ apInvoiceId: result.invoice_id }, data.line_items);
          } catch (carbonError) {
            captureException(carbonError as Error, {
              context: 'AP invoice carbon emissions create',
              invoiceId: result.invoice_id,
            });
          }
        }

        // Upload new attachments
        const newFiles = attachments.filter(a => a.file).map(a => a.file);
        const apInvoiceIdToUse = result?.invoice_id;
        if (apInvoiceIdToUse && newFiles.length > 0) {
          try {
            await uploadAttachments.mutateAsync({
              organizationId: selectedOrganization.id,
              apInvoiceId: apInvoiceIdToUse,
              files: newFiles,
              attachmentType: 'invoice',
              entityType: 'ap-invoices',
              entityId: apInvoiceIdToUse,
            });
          } catch (attachmentError) {
            toast.warning(t('billing.toast.someAttachmentsFailed'));
            captureException(attachmentError as Error, {
              context: 'AP invoice attachment upload (create mode)',
              invoiceId: apInvoiceIdToUse,
              fileCount: newFiles.length,
            });
          }
        }
      }

      // Call invoice created callback with result data (for inbox integration)
      if (onInvoiceCreated && createResult) {
        await onInvoiceCreated(createResult);
      }

      clearCarbonData();
      setAttachments([]);
      form.reset();
      onSuccess?.();
    } catch (error) {
      // Check if this is a re-approval confirmation request
      const errorMessage = (error as Error)?.message || '';
      if (errorMessage.startsWith('REQUIRES_CONFIRMATION:')) {
        const status = errorMessage.replace('REQUIRES_CONFIRMATION:', '');
        setCurrentApprovalStatus(status);
        setPendingFormData(data);
        setAutoReapprove(true); // fresh default each time the dialog opens
        setShowReapprovalConfirm(true);
        return;
      }
      // Editing a paid bill is allowed — EXCEPT moving the AP/liability account
      // or fund, because the existing payment sits on the old account. Offer to
      // migrate the payment(s) to the new account (reverse + re-post). If any
      // payment came from the bank feed it can't be reversed here, so guide the
      // user to the feed instead of opening a flow that would half-complete.
      if (errorMessage.startsWith('PAYMENT_ACCOUNT_CHANGE:')) {
        if (hasImportLinkedPayment) {
          toast.error(t('billing.editMovePayments.blockedTitle'), {
            description: t('billing.editMovePayments.blockedDescription'),
          });
          return;
        }
        setPendingFormData(data);
        setShowMovePaymentsConfirm(true);
        return;
      }
      // The mutation hook already toasted the translated tax-rate refusal.
      if (isTaxRateRefusal(error)) return;
      if (isEditMode) {
        toast.errorTemplates.update('invoice');
      } else {
        toast.errorTemplates.create('invoice');
      }
      captureException(error as Error, {
        context: isEditMode ? 'AP invoice update' : 'AP invoice create',
        invoiceId: invoiceId || 'new',
      });
    }
  };

  // Handler for confirmed re-approval (when user accepts the warning)
  const handleConfirmedReapproval = useCallback(async () => {
    if (!pendingFormData || !invoiceId || !selectedOrganization?.id) return;

    setShowReapprovalConfirm(false);

    try {
      const invoiceData = buildInvoiceData(pendingFormData);

      await updateAPInvoice.mutateAsync({
        organizationId: selectedOrganization!.id,
        invoiceId,
        data: invoiceData,
        forceUpdate: true,
        silent: true, // this handler owns the failure message (see catch)
      });

      // Force re-approval voids the ledger transaction and resets the bill to
      // pending (void_ap_invoice nulls ledger_transaction_id), so re-save carbon
      // keyed by the invoice id — it re-graduates on the next approval. Carbon
      // rows still attached to the now-voided original transaction are left as-is
      // (same orphan gap as delete_ap_invoice; out of scope here).
      try {
        // The unified RPC deletes prior rows for this invoice (including any
        // graduated onto the now-voided transaction, which keep ap_invoice_id)
        // by key before inserting.
        await saveCarbonEmissions({ apInvoiceId: invoiceId }, pendingFormData.line_items);
      } catch (carbonError) {
        captureException(carbonError as Error, {
          context: 'AP invoice carbon emissions force re-approval',
          invoiceId,
        });
      }

      // Auto re-approve (default on): the force-edit left the bill 'pending'
      // with its posting voided. Re-approving now re-posts the liability and
      // graduates the carbon rows just re-saved (approve_ap_invoice keys them by
      // ap_invoice_id) — so it must run AFTER saveCarbonEmissions. If the user
      // opted out, the bill stays pending and (when it has payments) the dialog
      // already warned that the payment would dangle until re-approval.
      if (autoReapprove) {
        try {
          await approveAPInvoice.mutateAsync({
            organizationId: selectedOrganization!.id,
            invoiceId,
            silent: true, // the try/catch below shows autoApproveFailed on error
          });
        } catch (approveError) {
          // The edit itself succeeded; only the re-approval failed, so the bill
          // is saved but sits 'pending' (a dangling payment if it had one). Tell
          // the user it needs manual approval rather than reporting the edit as
          // lost. approve surfaces PAYMENT_ACCOUNT_MISMATCH here only as a
          // backstop — the pre-flight blocks an account/fund change earlier.
          toast.error(t('billing.reapprovalDialog.autoApproveFailedTitle'), {
            description: t('billing.reapprovalDialog.autoApproveFailedDescription'),
          });
          captureException(approveError as Error, {
            context: 'AP invoice auto re-approval after force-edit',
            invoiceId,
          });
        }
      }

      clearCarbonData();
      setAttachments([]);
      setPendingFormData(null);
      setCurrentApprovalStatus(null);
      form.reset();
      onSuccess?.();
    } catch (error) {
      // The update ran `silent`, so this catch owns the message — the
      // translated tax-rate refusal when it is one.
      if (toastTaxRateRefusal(error, t)) return;
      toast.errorTemplates.update('invoice');
      captureException(error as Error, {
        context: 'AP invoice force-update (re-approval)',
        invoiceId,
      });
    }
  }, [pendingFormData, invoiceId, selectedOrganization?.id, updateAPInvoice, approveAPInvoice, autoReapprove, t, clearCarbonData, form, onSuccess, buildInvoiceData, saveCarbonEmissions, invoice?.ledger_transaction_id]);

  // Handler for the "move payments" flow: the edit changes a paid bill's AP
  // account/fund, so the existing payment(s) must be migrated to the new
  // account. Orchestrated as void → update(new account) → repoint → re-approve.
  // repoint runs BEFORE re-approval so approve's PAYMENT_ACCOUNT_MISMATCH
  // backstop passes; re-approval is mandatory (a moved-payment bill must not be
  // left pending/unposted). Not a single transaction — a mid-flight failure
  // leaves the bill 'pending' (recoverable by re-approving), surfaced clearly.
  const handleConfirmedMovePayments = useCallback(async () => {
    if (!pendingFormData || !invoiceId || !selectedOrganization?.id) return;
    setShowMovePaymentsConfirm(false);
    const orgId = selectedOrganization.id;

    try {
      const invoiceData = buildInvoiceData(pendingFormData);

      // 1) Void the old posting + write the new account/fund onto the header.
      //    allowAccountChange bypasses the paid-bill account-change guard — we
      //    migrate the payments ourselves in step 3.
      await updateAPInvoice.mutateAsync({
        organizationId: orgId,
        invoiceId,
        data: invoiceData,
        forceUpdate: true,
        allowAccountChange: true,
        silent: true, // this handler owns the failure message (see catch)
      });

      // 2) Re-save carbon (keyed by invoice id) so re-approval graduates it.
      try {
        await saveCarbonEmissions({ apInvoiceId: invoiceId }, pendingFormData.line_items);
      } catch (carbonError) {
        captureException(carbonError as Error, {
          context: 'AP invoice carbon emissions move-payments',
          invoiceId,
        });
      }

      // 3) Migrate the payment(s) to the new account BEFORE re-approval.
      await repointAPPayments.mutateAsync({
        organizationId: orgId,
        invoiceId,
        newApAccountId: invoiceData.ap_account_id,
        newFundId: invoiceData.fund_id ?? null,
      });

      // 4) Re-approve (mandatory) — re-posts the liability on the new account.
      await approveAPInvoice.mutateAsync({ organizationId: orgId, invoiceId, silent: true });

      toast.success(t('billing.editMovePayments.successTitle'), {
        description: t('billing.editMovePayments.successDescription'),
      });

      clearCarbonData();
      setAttachments([]);
      setPendingFormData(null);
      form.reset();
      onSuccess?.();
    } catch (error) {
      const msg = (error as Error)?.message || '';
      if (msg === 'IMPORT_LINKED') {
        toast.error(t('billing.undoPayment.importLinkedTitle'), {
          description: t('billing.undoPayment.importLinkedDescription'),
        });
        return;
      }
      // Step 1 (the silent update) refused a line's tax rate: nothing moved.
      if (toastTaxRateRefusal(error, t)) return;
      // The void/update likely already ran, so the bill may be 'pending' now.
      // Tell the user rather than reporting the edit as lost.
      toast.error(t('billing.editMovePayments.failedTitle'), {
        description: t('billing.editMovePayments.failedDescription'),
      });
      captureException(error as Error, {
        context: 'AP invoice move-payments flow',
        invoiceId,
      });
    }
  }, [pendingFormData, invoiceId, selectedOrganization?.id, buildInvoiceData, updateAPInvoice, saveCarbonEmissions, repointAPPayments, approveAPInvoice, t, clearCarbonData, form, onSuccess]);

  // "Make this recurring" ticked/unticked. The bill number is the one field
  // the schedule changes the meaning of: suggest the tokenised default when
  // the field is empty, and take it back on untick only if it is still ours.
  const handleRecurringToggle = useCallback((enabled: boolean) => {
    const current = form.getValues('invoice_number');
    if (enabled && !current) {
      form.setValue('invoice_number', AP_BILL_NUMBER_TOKEN_DEFAULT, { shouldDirty: true });
    } else if (!enabled && current === AP_BILL_NUMBER_TOKEN_DEFAULT) {
      form.setValue('invoice_number', '', { shouldDirty: true });
    }
    onRecurringChange?.(enabled);
  }, [form, onRecurringChange]);

  // What the schedule seeds from the moment the box is ticked — read live.
  const getRecurrenceSeed = useCallback(() => {
    const values = form.getValues();
    const payee =
      values.payee_type === 'business'
        ? businesses?.find((b) => b.id === values.business_id)?.name
        : (() => {
            const person = people?.find((p) => p.id === values.person_id);
            return person ? `${person.first_name} ${person.last_name}` : undefined;
          })();
    return {
      documentDate: values.invoice_date,
      nameBase: payee ?? values.memo,
      dueDays: dueDaysFromTerms(values.payment_terms, values.invoice_date, values.due_date),
    };
  }, [form, businesses, people]);

  // Stable wrapper function that calls the ref
  const onSubmit = useCallback(async (data: FormData) => {
    await onSubmitRef.current?.(data);
  }, []);

  // Expose submit handler to parent - only on mount to prevent re-render loops
  // The handler uses refs internally so it always has access to latest state
  useEffect(() => {
    if (onSubmitReady) {
      onSubmitReady(() => form.handleSubmit(onSubmit)());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing invoice data when in edit mode. Hydrate ONCE per invoice
  // id (the ref below): the edit dialog now feeds this form a LIVE
  // useAPInvoiceById result, so a background refetch (focus, a mutation's
  // ['apInvoice'] invalidation) mints a new `invoice` object identity —
  // without the guard, the unconditional form.reset() below would silently
  // discard the user's unsaved edits. Same once-guard pattern as
  // appliedParsedDataRef above. Remount (the dialog is conditionally
  // mounted) naturally re-hydrates.
  // One mapping for edit AND clone hydration (kept in sync by construction).
  const invoiceToFormData = useCallback(
    (source: APInvoice) => ({
      payee_type: (source.business_id ? 'business' : 'person') as 'business' | 'person',
      business_id: source.business_id || '',
      business_name: source.business_id || '', // SearchableSelect will display the label for UUID
      person_id: source.person_id || '',
      invoice_number: source.invoice_number,
      invoice_date: source.invoice_date.slice(0, 10),
      due_date: source.due_date.slice(0, 10),
      payment_terms: 'custom' as string,
      memo: source.description || '',
      // Historical fallback: legacy invoices stored the reference on ledger_transactions.
      // New writes go to accounts_payable.purchase_order_number; the ledger fallback only
      // hydrates approved invoices created before that fix landed.
      reference: source.purchase_order_number || source.ledger_transaction?.reference || '',
      ap_account_id: String(source.metadata?.ap_account_id ?? ''),
      project_id: source.project_id || '',
      fund_id: String(source.metadata?.fund_id ?? ''),
      line_items: source.line_items?.map(item => {
        const { taxRate, isReverseCharge } = toTaxRateType(item.tax_treatment, item.tax_rate ?? 0);
        return {
          expense_account_id: item.expense_account_id || '',
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: taxRate as TaxRateType,
          tax_rate_id: item.tax_rate_id || '',
          tax_inclusive: false,
          is_reverse_charge: isReverseCharge,
          exemption_reason: item.exemption_reason || '',
          exemption_reason_other: item.exemption_reason_other || '',
          wkr_enabled: !!(item.id && wkrByLineId?.[item.id]),
          wkr_category: (item.id && wkrByLineId?.[item.id]) || '',
        };
      }) || [
        {
          expense_account_id: '',
          description: '',
          quantity: 1,
          unit_price: 0,
          tax_rate: 0 as TaxRateType,
          tax_rate_id: '',
          tax_inclusive: false,
          is_reverse_charge: false,
          exemption_reason: '',
          exemption_reason_other: '',
          // Placeholder 0 — InvoiceLineItems fills the org default rate.
          tax_rate_unset: true,
        },
      ],
      // Advanced VAT Options
      self_billing: source.self_billing ?? false,
      additional_vat_notes: source.additional_vat_notes || '',
    }),
    [wkrByLineId],
  );

  const hydratedInvoiceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode && invoice && accounts && businesses && people && projects && funds && !isLoadingData && !isLoadingWkr) {
      if (hydratedInvoiceIdRef.current === invoice.id) return;
      hydratedInvoiceIdRef.current = invoice.id;
      const formData = invoiceToFormData(invoice);
      setTimeout(() => {
        form.reset(formData);
        setIsFormReady(true);
      }, 0);
    }
  }, [isEditMode, invoice, accounts, businesses, people, projects, funds, isLoadingData, isLoadingWkr, invoiceToFormData, form]);

  // Clone mode: same mapping, saved as NEW. Dates move to today (the due date
  // keeps its distance), the vendor's bill number is cleared (the recurring
  // default `BILL-{{YYYY}}{{MM}}` is seeded by the tick handler / defaults),
  // and the recurring fields from `defaultValues` are preserved. `reset`
  // makes these the new defaults, so a freshly cloned form is not dirty.
  const clonedFromIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode || !cloneFrom || !accounts || !businesses || !people || !projects || !funds || isLoadingData || isLoadingWkr) return;
    if (clonedFromIdRef.current === cloneFrom.id) return;
    clonedFromIdRef.current = cloneFrom.id;
    const mapped = invoiceToFormData(cloneFrom);
    const today = format(new Date(), 'yyyy-MM-dd');
    // The source's payment distance in calendar days (DST-safe), re-applied from today.
    const termDays = dueDaysFromTerms('custom', cloneFrom.invoice_date.slice(0, 10), cloneFrom.due_date.slice(0, 10)) ?? 30;
    const current = form.getValues();
    form.reset({
      ...current,
      ...mapped,
      invoice_number: current.recurring_enabled ? current.invoice_number : '',
      invoice_date: today,
      due_date: formatLocalISODate(addDays(new Date(), termDays)) ?? today,
      payment_terms: 'custom',
      recurring_enabled: current.recurring_enabled,
      recurrence: current.recurrence,
    });
    setIsFormReady(true);
    // "Make recurring" from a row: the box starts ticked with an EMPTY name
    // (the New-menu flow leaves it for the user) — seed it from the cloned
    // payee now that the form holds the cloned values. Still a reset, not a
    // setValue, so the form stays pristine.
    const afterClone = form.getValues();
    if (afterClone.recurring_enabled && afterClone.recurrence && !afterClone.recurrence.name) {
      const seed = getRecurrenceSeed();
      form.reset({
        ...afterClone,
        recurrence: {
          ...afterClone.recurrence,
          name: suggestTemplateName(seed.nameBase, afterClone.recurrence.frequency, (key, vars) => String(t(key, vars))),
        },
      });
    }
  }, [isEditMode, cloneFrom, accounts, businesses, people, projects, funds, isLoadingData, isLoadingWkr, invoiceToFormData, form, getRecurrenceSeed, t]);

  // Filter accounts by category
  const liabilityAccounts = useMemo(() => {
    return accounts?.filter(acc => acc.category?.category_name === 'Liabilities') || [];
  }, [accounts]);

  // Auto-select AP account (only for new invoices)
  useEffect(() => {
    if (isEditMode) return;
    if (!liabilityAccounts.length || form.getValues('ap_account_id')) return;

    // Priority 1: System AP account
    const systemApAccount = liabilityAccounts.find(acc =>
      acc.system_account_type === 'accounts_payable'
    );
    if (systemApAccount) {
      form.setValue('ap_account_id', systemApAccount.id);
      return;
    }

    // Priority 2: Type match + currency match
    const defaultCurrency = selectedOrganization?.currency;
    if (defaultCurrency) {
      const currencyMatch = liabilityAccounts.find(acc =>
        acc.type?.type_name === 'Accounts Payable' &&
        (acc.currency === defaultCurrency || (!acc.currency && defaultCurrency))
      );
      if (currencyMatch) {
        form.setValue('ap_account_id', currencyMatch.id);
        return;
      }
    }
  }, [liabilityAccounts, selectedOrganization?.currency, form, isEditMode]);

  if ((isEditMode || !!cloneFrom) && !isFormReady) {
    return <div className="p-4">{t('billing.form.loadingInvoice')}</div>;
  }

  return (
    <Form {...form}>
      <div className="space-y-8">
        {/* Metadata row: internal associations (not part of the printed bill) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="project_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('billing.form.projectOptional')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                      <SelectValue placeholder={t('billing.form.selectProject')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">{t('billing.form.noProject')}</SelectItem>
                    {projects?.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fund_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('billing.form.fundOptional')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p className="text-sm">
                          {t('billing.form.fundTooltip')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <FundSelect
                  formControl
                  funds={funds ?? []}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder={t('billing.form.selectFund')}
                  specialOptions={[{ value: 'none', label: t('billing.form.noFund') }]}
                  className={fieldState.error ? 'border-destructive' : undefined}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ap_account_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('billing.payableAccount')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                <FormControl>
                  <AccountSelect
                    accounts={liabilityAccounts}
                    showCurrency={showAccountCurrency}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('billing.form.selectAccount')}
                    className={fieldState.error ? "border-destructive" : ""}
                    prioritizeCategory="Liabilities"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Bill document card */}
        <Card>
          <CardContent className="pt-6 space-y-8">
            {/* SECTION: FROM (Organization Info) */}
            <div>
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">{t('billing.form.billTitle')}</h2>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('invoices.form.from')}
                  </h3>
                  <p className="font-semibold text-lg">{selectedOrganization?.name}</p>
                  <OrganizationIdentityDisplay
                    orgIdentity={orgIdentity}
                    vatNumber={taxSettings?.tax_identification_number}
                    orgCountry={selectedOrganization?.country}
                    taxJurisdiction={taxSettings?.primary_tax_jurisdiction}
                    showBanking={false} // Org is the payer; no need to show IBAN to vendor
                  />
                </div>
                {selectedOrganization?.logo_small && (
                  <EntityImage
                    imagePath={selectedOrganization.logo_small}
                    alt={selectedOrganization.name}
                    className="h-24 w-24 mt-5"
                    variant="square"
                  />
                )}
              </div>
            </div>

            {/* Full-bleed paper band: Vendor + Bill Details */}
            <div className="-mx-6 px-6 py-6 bg-releve-gray-light">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: VENDOR */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">
                    {t('billing.vendor')}
                  </h3>
                  <div className="space-y-4">
                    {/* Payee Type Selection */}
                    <FormField
                      control={form.control}
                      name="payee_type"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('billing.form.payeeType')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}>
                                <SelectValue placeholder={t('billing.form.selectPayeeType')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="business">
                                <div className="flex items-center gap-2">
                                  <Building className="h-4 w-4" />
                                  {t('billing.form.businessVendor')}
                                </div>
                              </SelectItem>
                              <SelectItem value="person">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  {t('billing.form.person')}
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    {/* Conditional Business/Person Selection */}
                    {payeeType === 'business' ? (
                      <FormField
                        control={form.control}
                        name="business_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('billing.form.vendorBusiness')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}>
                                  <SelectValue placeholder={t('billing.form.selectVendor')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {(businesses ?? []).map((business) => (
                                  <SelectItem key={business.id} value={business.id}>
                                    {business.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    ) : payeeType === 'person' ? (
                      <FormField
                        control={form.control}
                        name="person_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('billing.form.person')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}>
                                  <SelectValue placeholder={t('billing.form.selectPerson')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {people?.map((person) => (
                                  <SelectItem key={person.id} value={person.id}>
                                    {person.first_name} {person.last_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    ) : null}
                  </div>

                  {/* Selected vendor info card */}
                  {selectedVendorInfo && (
                    <div className="mt-3 p-3 bg-white/60 rounded-md">
                      <p className="text-sm font-medium text-gray-800">{selectedVendorInfo.name}</p>
                      {selectedVendorInfo.email && (
                        <p className="text-xs text-gray-600 mt-1">{selectedVendorInfo.email}</p>
                      )}
                      {payeeType === 'business' && vendorIdentity && (
                        // This card sits in the fixed-light "paper" band; force dark text so
                        // VendorIdentityDisplay's theme-aware colors stay readable in dark mode.
                        <div className="text-gray-700 [&_.text-muted-foreground]:text-gray-500">
                          <VendorIdentityDisplay vendorIdentity={vendorIdentity} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column: BILL DETAILS */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">
                    {t('invoices.form.invoiceDetails')}
                  </h3>
                  <div className="space-y-4">
                    {/* Row 1: Invoice Number, Bill Date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="invoice_number"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.invoiceNumber')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <Input
                                placeholder={t('billing.form.vendorInvoiceNumberPlaceholder')}
                                className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                                {...field}
                              />
                            </FormControl>
                            {recurringEnabled && (
                              // Frozen paper band → pinned hint colour (FULLSCREEN_FORM_MODALS.md).
                              <p className="text-xs text-gray-500 mt-1">
                                {t('recurring.form.billNumberHint', {
                                  tokens: RECURRING_TEXT_TOKENS_HINT_VALUE,
                                  example: AP_BILL_NUMBER_TOKEN_DEFAULT,
                                })}
                              </p>
                            )}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="invoice_date"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              {/* Date-only since invoice_date became a plain `date` column — the
                                  billing.form.billDateTime ("Invoice Date & Time") label no longer applies. */}
                              <FormLabel className="text-gray-700 font-extralight">{t('billing.columns.invoiceDate')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DatePicker
                                value={parseLocalISODate(field.value)}
                                onChange={(date) => {
                                  field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
                                  handleInvoiceDateChange(date);
                                }}
                                disabled={recurringEnabled}
                                className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              />
                            </FormControl>
                            {recurringEnabled && (
                              <p className="text-xs text-gray-500 mt-1">{t('recurring.form.setBySchedule')}</p>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Row 2: Due Date, Payment Terms */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="due_date"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.dueDate')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DatePicker
                                value={parseLocalISODate(field.value)}
                                onChange={(date) => {
                                  field.onChange(formatLocalISODate(date) || '');
                                  // Manual due-date edits switch terms to Custom so the
                                  // value isn't recalculated away by later date changes
                                  form.setValue('payment_terms', 'custom');
                                }}
                                disabled={recurringEnabled}
                                className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              />
                            </FormControl>
                            {recurringEnabled && (
                              <p className="text-xs text-gray-500 mt-1">{t('recurring.form.setBySchedule')}</p>
                            )}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="payment_terms"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.paymentTerms')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select
                              onValueChange={handlePaymentTermsChange}
                              value={field.value}
                              disabled={recurringEnabled}
                            >
                              <FormControl>
                                <SelectTrigger className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}>
                                  <SelectValue placeholder={t('billing.form.selectTerms')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {PAYMENT_TERMS_OPTIONS.map((term) => (
                                  <SelectItem key={term.value} value={term.value}>
                                    {term.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Row 3: Reference */}
                    <FormField
                      control={form.control}
                      name="reference"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('billing.form.reference')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Input
                              placeholder={t('billing.form.referencePlaceholder')}
                              className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Currency conversion notice for scanned foreign-currency documents */}
            {conversionBanner?.type === 'estimated' && (
              <Alert className="bg-info/10 text-info border-info/30">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t('documentConversion.estimatedBanner', {
                    currency: conversionBanner.docCurrency,
                    rate: Number(conversionBanner.rate.toPrecision(6)),
                    date: conversionBanner.rateDate,
                  })}
                </AlertDescription>
              </Alert>
            )}
            {conversionBanner?.type === 'unavailable' && (
              <Alert className="bg-warning/10 text-warning border-warning/30">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t('documentConversion.rateUnavailableBanner', {
                    currency: conversionBanner.docCurrency,
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* SECTION: LINE ITEMS */}
            <InvoiceLineItems
              asOfDate={form.watch('invoice_date')}
              type="ap"
              form={form}
              accounts={stableAccounts}
              currency={
                // Rate lookup failed → amounts are still in the document's
                // currency; label them as such rather than with the org symbol.
                conversionBanner?.type === 'unavailable'
                  ? conversionBanner.docCurrency
                  : undefined
              }
              carbonDataByLineItem={carbonDataByLineItem}
              onCarbonDataChange={updateCarbonData}
              pinnedAccountIds={suspenseAccountId ? [suspenseAccountId] : undefined}
              enableWkr
            />
          </CardContent>
        </Card>

        {/* SECTION: ADVANCED VAT OPTIONS */}
        <Collapsible open={vatOptionsOpen} onOpenChange={setVatOptionsOpen} className="border rounded-lg">
          <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 hover:bg-muted/50 transition-colors">
            <ChevronRight className={`h-4 w-4 transition-transform ${vatOptionsOpen ? 'rotate-90' : ''}`} />
            <span className="text-sm font-medium">{t('vat.advancedOptions.toggle')}</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4 space-y-4">
            <FormField
              control={form.control}
              name="self_billing"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">{t('vat.advancedOptions.selfBilling.label')}</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      {t('vat.advancedOptions.selfBilling.hint')}
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="additional_vat_notes"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="text-sm">{t('vat.advancedOptions.additionalNotes.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('vat.advancedOptions.additionalNotes.placeholder')}
                      className={fieldState.error ? "border-destructive" : ""}
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CollapsibleContent>
        </Collapsible>

        {/* SECTION: MAKE THIS RECURRING — sits above the attachments so the
            upload zone can be disabled while ticked (attachments are not copied
            to generated documents). Never in document-edit mode. */}
        {(allowRecurring || isTemplateMode) && !isEditMode && (
          <RecurringSection
            documentType="ap_bill"
            orgToday={orgToday}
            getSeed={getRecurrenceSeed}
            template={recurringTemplate}
            onEnabledChange={handleRecurringToggle}
          />
        )}

        {/* SECTION: ADDITIONAL INFO */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            {t('billing.form.additionalInfo')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="memo"
              render={({ field, fieldState }) => (
                <FormItem className="space-y-2">
                  <FormLabel>{t('billing.form.memoOptional')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('billing.form.memoPlaceholder')}
                      className={fieldState.error ? "border-destructive" : ""}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>{t('invoices.form.attachments')}</FormLabel>
              {recurringEnabled && (
                <p className="text-xs text-muted-foreground">{t('recurring.form.notes.notCopied')}</p>
              )}
              <Card>
                <CardContent className="p-3">
                  <FileAttachments
                    disabled={recurringEnabled}
                    value={attachments}
                    onChange={setAttachments}
                    onDeleteExisting={handleDeleteExisting}
                    maxFiles={10}
                    acceptedTypes={['image/*', 'application/pdf', '.doc', '.docx', '.xls', '.xlsx']}
                    className="h-full"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {isTemplateMode && onRequestDelete && (
          <DangerZone
            title={t('recurring.dangerZone.heading')}
            actionTitle={t('recurring.dangerZone.title')}
            description={t('recurring.dangerZone.description')}
            buttonLabel={t('recurring.dangerZone.action')}
            onAction={onRequestDelete}
          />
        )}
      </div>

      {/* Re-approval Confirmation Dialog */}
      <AlertDialog open={showReapprovalConfirm} onOpenChange={setShowReapprovalConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('billing.reapprovalDialog.title', { status: currentApprovalStatus })}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2" asChild>
              <div>
                <p>
                  <Trans
                    i18nKey="billing.reapprovalDialog.body1"
                    values={{ status: currentApprovalStatus }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p>
                  <Trans
                    i18nKey="billing.reapprovalDialog.body2"
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p className="text-amber-600 font-medium">
                  {t('billing.reapprovalDialog.warning')}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Auto re-approve (default on) so the bill never lingers in 'pending'
              with a posted-but-unapplied (dangling) payment. Unchecking it on a
              paid bill surfaces the dangling-payment warning below. */}
          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={autoReapprove}
                onCheckedChange={(checked) => setAutoReapprove(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm">
                {t('billing.reapprovalDialog.autoReapproveLabel')}
              </span>
            </label>
            {(invoice?.amount_paid ?? 0) > 0 && !autoReapprove && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                <Trans
                  i18nKey="billing.reapprovalDialog.danglingWarning"
                  count={invoice?.payment_transactions?.length ?? 0}
                  values={{ count: invoice?.payment_transactions?.length ?? 0 }}
                  components={{
                    strong: <strong />,
                    amount: <CurrencyDisplay value={invoice?.amount_paid ?? 0} />,
                  }}
                />
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPendingFormData(null);
              setCurrentApprovalStatus(null);
            }}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedReapproval}>
              {autoReapprove
                ? t('billing.reapprovalDialog.confirmAndReapprove')
                : t('billing.reapprovalDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move-payments Confirmation Dialog — the edit changes a paid bill's AP
          account/fund, so the payment(s) are migrated to the new account. */}
      <AlertDialog open={showMovePaymentsConfirm} onOpenChange={setShowMovePaymentsConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('billing.editMovePayments.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2" asChild>
              <div>
                <p>
                  <Trans
                    i18nKey="billing.editMovePayments.body"
                    count={invoice?.payment_transactions?.length ?? 0}
                    values={{ count: invoice?.payment_transactions?.length ?? 0 }}
                    components={{
                      strong: <strong />,
                      amount: <CurrencyDisplay value={invoice?.amount_paid ?? 0} />,
                    }}
                  />
                </p>
                <p>{t('billing.editMovePayments.body2')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingFormData(null)}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedMovePayments}>
              {t('billing.editMovePayments.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
};
